import emailjs from 'emailjs-com';
import React, { useState, type ChangeEvent } from 'react';
import { useTranslation } from 'react-i18next';

import classNames from 'classnames';
import Button from './Button';
import Input from './Input';

import { type PartnerResult } from '../utils/constants';
import { generateEmailHtml } from '../utils/emailTemplate';

interface EmailFormProps {
  result: PartnerResult;
  allAnswers: string[];
  showResults: boolean;
  setShowResults: (show: boolean) => void;
}

interface UserData {
  name: string;
  email: string;
}

const EmailForm: React.FC<EmailFormProps> = ({
  result,
  allAnswers,
  showResults,
  setShowResults
}) => {
  const [emailValid, setEmailValid] = useState<boolean>(true);
  const [formError, setFormError] = useState<boolean>(false);
  const { t, i18n } = useTranslation();

  const [userData, setUserData] = useState<UserData>({
    name: '',
    email: ''
  });

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>): void => {
    const { name, value } = event.target;
    setUserData({
      ...userData,
      [name]: value
    });

    setFormError(false);

    if (name === 'email') {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      setEmailValid(emailRegex.test(value));
    }
  };

  const handleSubmit = (): void => {
    if (userData.name && userData.email && emailValid) {
      setShowResults(true);
      setFormError(false);
      const emailHtml = generateEmailHtml(
        result,
        userData.name,
        allAnswers,
        i18n.language
      );
      sendEmail(emailHtml);
    } else {
      setFormError(true);
    }
  };

  const sendEmail = (htmlContent: string): void => {
    const publicKey = 'jl8F2dQIYXoXzo-Dy';
    const serviceId = 'service_3fnkp79';
    const templateId = 'template_7kr48vf';

    // Send to User
    const userParams = {
      from_name: 'Anderson Miranda Coaching',
      to_name: userData.name,
      to_email: userData.email,
      message_html: htmlContent,
      subject: t('emailTemplate.subject')
    };

    emailjs.send(serviceId, templateId, userParams, publicKey).then(
      (response) => {
        console.log('SUCCESS USER!', response.status, response.text);
      },
      (err) => {
        console.log('FAILED USER...', err);
      }
    );
  };

  return (
    <section
      className={classNames(
        'mt-8 flex flex-col gap-4 w-full max-w-[370px]',
        showResults ? 'hidden' : 'block'
      )}
    >
      <p>{t('emailForm.preparing')}</p>
      <p>{t('emailForm.intro')}</p>
      <Input
        type="text"
        name="name"
        placeholder={t('emailForm.namePlaceholder')}
        value={userData.name}
        onChange={handleInputChange}
      />
      <Input
        type="email"
        name="email"
        placeholder={t('emailForm.emailPlaceholder')}
        value={userData.email}
        onChange={handleInputChange}
      />
      {!emailValid && (
        <p className="text-sm text-primary">{t('emailForm.invalidEmail')}</p>
      )}
      {formError && (
        <p className="text-sm text-primary">{t('emailForm.allFields')}</p>
      )}
      <Button
        onClick={() => {
          handleSubmit();
        }}
      >
        {t('emailForm.cta')}
      </Button>
    </section>
  );
};

export default EmailForm;
